import { DefaultError, useMutation, useQuery } from '@tanstack/react-query';
import { useQueryToast } from 'hooks';
import { useChunkUpload } from 'hooks/useChunkUpload';
import i18next from 'i18next';
import { apiClient } from 'utils';
import { useResponseHandler } from 'utils/service/responseHandler';
import { IVideoFileUploadComplete } from './types';

export const useGetVideoFile = (id: string, enabled?: boolean) => {
  const { responseStreamHandler } = useResponseHandler();

  return useQuery<string, DefaultError>({
    queryKey: ['video-file', id],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: `Video/${id}/File`,
        headers: {
          'Content-Range': 'bytes=0-99999',
        },
      });

      const blob = await response.blob();

      return URL.createObjectURL(blob);
    },
    enabled,
    retry: false,
  });
};

export const usePostVideoChunkedFile = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast(
    'usePostVideoChunkedFile'
  );

  const { uploadChunkedFiles } = useChunkUpload('Post Video');

  return useMutation<{}, DefaultError, IVideoFileUploadComplete>({
    mutationFn: async ({ file, videoId }) => {
      loading(
        i18next.t('service-message.add.loading', {
          what: i18next.t('video.file-what'),
        })
      );

      const { totalChunks, importId } = await uploadChunkedFiles(
        file,
        undefined
      );

      const finalResponse = await apiClient({
        method: 'POST',
        url: 'Video/File/UploadComplete',
        params: { importId, totalChunks, videoId },
      });

      return responseHandler(finalResponse, {
        groupName: 'usePostVideoChunkedFile',
      });
    },
    onSuccess: data => {
      success(
        i18next.t('service-message.add.success', {
          what: i18next.t('video.file-what'),
        })
      );
    },
    onError: dismiss,
  });
};

export const usePutVideoChunkedFile = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast('usePutVideoChunkedFile');

  const { progress, resetProgress, uploadChunkedFiles } =
    useChunkUpload('Put Video');

  return useMutation<{}, DefaultError, IVideoFileUploadComplete>({
    mutationFn: async ({ file, videoId }) => {
      loading(
        i18next.t('service-message.update.loading', {
          what: i18next.t('video.file-what'),
        })
      );

      const { totalChunks, importId } = await uploadChunkedFiles(
        file,
        undefined
      );

      const finalResponse = await apiClient({
        method: 'PUT',
        url: 'Video/File/UpdateComplete',
        params: { importId, totalChunks, videoId },
      });

      return responseHandler(finalResponse, {
        groupName: 'usePutVideoChunkedFile',
      });
    },
    onSuccess: data => {
      success(
        i18next.t('service-message.update.success', {
          what: i18next.t('video.file-what'),
        })
      );
    },
    onError: dismiss,
  });
};

export const useDeleteVideoFile = (videoId: string) => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast('useDeleteVideoFile');
  return useMutation<string, DefaultError>({
    mutationFn: async () => {
      loading(
        i18next.t('service-message.remove.loading', {
          what: i18next.t('video.file-what'),
        })
      );

      const response = await apiClient({
        method: 'DELETE',
        url: 'Video/File',
        params: { videoId },
        withContentType: false,
      });

      return responseHandler(response, {
        groupName: `useDeleteVideoFile - ${videoId}`,
      });
    },
    onSuccess: data => {
      success(
        i18next.t('service-message.remove.success', {
          what: i18next.t('video.file-what'),
        })
      );
    },
    onError: dismiss,
  });
};
